import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import {
  CalculatedIngredientItem,
  CalculatedProductSalesUnitItem,
  GenericIngredientItem,
  GenericIngredientOption,
  IngredientDraftRow
} from "../types/recipeAddIngredient.types";
import { createCompositeKey } from "./recipeAddIngredient.utils";

const formatOwnProductName = (item: CalculatedProductSalesUnitItem) => {
  if (item.productName && item.salesUnitName) {
    return `${item.productName} - ${item.salesUnitName}`;
  }

  if (item.productName) {
    return item.productName;
  }

  if (item.salesUnitName) {
    return item.salesUnitName;
  }

  return item.salesUnitId ? `Product unit ${item.salesUnitId}` : "Own product";
};

const createExistingDraftRow = ({
  ingredientType,
  displayName,
  percentage,
  sourceId,
  productUnitKey = "",
  ingredientId = ""
}: {
  ingredientType: RecipeIngredientType;
  displayName: string;
  percentage: number;
  sourceId: string;
  productUnitKey?: string;
  ingredientId?: string;
}): IngredientDraftRow => ({
  ingredientType,
  productUnitKey,
  ingredientId,
  percentage,
  descriptionOpen: false,
  saved: true,
  isExisting: true,
  sourceId,
  displayName
});

export const mapExistingIngredientsToDraftRows = (
  calculatedProductSalesUnit: CalculatedProductSalesUnitItem[],
  genericIngredient: GenericIngredientItem[],
  calculatedIngredient: CalculatedIngredientItem[],
  genericOptions: GenericIngredientOption[] = []
): IngredientDraftRow[] => {
  const ownProducts = calculatedProductSalesUnit.map(item =>
    createExistingDraftRow({
      ingredientType: RecipeIngredientType.OWN_PRODUCT,
      displayName: formatOwnProductName(item),
      percentage: Number(item.percentage) || 0,
      sourceId: item._id ?? item.salesUnitId,
      productUnitKey:
        item.productId && item.salesUnitId
          ? createCompositeKey(item.productId, item.salesUnitId)
          : ""
    })
  );

  const genericIngredients = genericIngredient.map(item => {
    const optionTitle = genericOptions.find(
      option => String(option.value) === String(item.ingredientId)
    )?.title;

    return createExistingDraftRow({
      ingredientType: RecipeIngredientType.GENERIC_INGREDIENT,
      displayName: optionTitle || item.ingredientName || `Ingredient ${item.ingredientId}`,
      percentage: Number(item.percentage) || 0,
      sourceId: item._id ?? String(item.ingredientId),
      ingredientId: String(item.ingredientId)
    });
  });

  const calculatedIngredients = calculatedIngredient.map((item, index) =>
    createExistingDraftRow({
      ingredientType: RecipeIngredientType.CALCULATED_INGREDIENT,
      displayName:
        item.ingredientName ||
        (item.ingredientId ? `Ingredient ${item.ingredientId}` : "Calculated ingredient"),
      percentage: Number(item.percentage) || 0,
      sourceId: item._id ?? item.ingredientId ?? `calculated-${index}`,
      ingredientId: item.ingredientId ? String(item.ingredientId) : ""
    })
  );

  return [...ownProducts, ...genericIngredients, ...calculatedIngredients];
};

export const buildInitialDraftRows = (
  calculatedProductSalesUnit: CalculatedProductSalesUnitItem[],
  genericIngredient: GenericIngredientItem[],
  calculatedIngredient: CalculatedIngredientItem[],
  createEmptyDraftRow: () => IngredientDraftRow,
  genericOptions: GenericIngredientOption[] = []
): IngredientDraftRow[] => [
  ...mapExistingIngredientsToDraftRows(
    calculatedProductSalesUnit,
    genericIngredient,
    calculatedIngredient,
    genericOptions
  ),
  createEmptyDraftRow()
];
